import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Loader2 } from "lucide-react";

export default function VendorResolutionDesk() {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendors, setVendors] = useState([]);
  const [pickedVendor, setPickedVendor] = useState(null);
  const [working, setWorking] = useState(false);

  const load = async () => {
    const list = await api.listCandidates({
      status: "NEEDS_REVIEW,BLOCKED,UPLOADED",
    });
    // only those that still need vendor resolution
    setCandidates(
      list.filter((c) =>
        ["UNRESOLVED", "NEW_VENDOR_REQUIRED", "AMBIGUOUS_MATCH", "SUGGESTED_MATCH", "CONFLICT_MATCH"].includes(
          c.vendor_resolution_state
        )
      )
    );
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let abort = false;
    (async () => {
      if (!vendorQuery.trim()) {
        setVendors([]);
        return;
      }
      try {
        const r = await api.searchVendors(vendorQuery);
        if (!abort) setVendors(r);
      } catch (_e) {
        /* ignore search errors */
      }
    })();
    return () => {
      abort = true;
    };
  }, [vendorQuery]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = async () => {
    if (!pickedVendor || selected.size === 0) {
      toast.error("Select a vendor and at least one candidate");
      return;
    }
    setWorking(true);
    let ok = 0;
    let fail = 0;
    for (const id of selected) {
      try {
        await api.updateCandidate(id, { selected_vendor_id: pickedVendor.id });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setWorking(false);
    toast.success(`Applied ${pickedVendor.name} to ${ok} candidate(s)${fail ? `, ${fail} failed` : ""}`);
    setSelected(new Set());
    await load();
  };

  const grouped = useMemo(() => {
    const m = new Map();
    for (const c of candidates) {
      const k = (c.extracted_vendor_name || "—").trim();
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [candidates]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-[28px] font-semibold text-archival-primary tracking-tight leading-tight">
          Vendor Resolution Desk
        </h1>
        <p className="text-[14px] text-archival-secondary mt-1">
          Resolve vendor issues across many candidates. Apply a vendor in bulk safely.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-archival rounded-md bg-archival-surface overflow-hidden">
          <div className="px-4 py-2.5 border-b border-archival flex items-center justify-between">
            <div className="text-[13px] font-semibold text-archival-primary">
              Candidates needing vendor resolution
            </div>
            <div className="text-[11px] text-archival-muted font-mono">
              {selected.size} selected · {candidates.length} total
            </div>
          </div>
          {grouped.length === 0 ? (
            <div className="py-16 text-center text-archival-muted text-[13px]">All vendors resolved.</div>
          ) : (
            <ul>
              {grouped.map(([extracted, rows]) => (
                <li key={extracted} className="border-b border-archival last:border-b-0">
                  <div className="px-4 py-2 bg-archival-soft text-[12px] uppercase tracking-wider text-archival-secondary font-semibold flex items-center justify-between">
                    <span>{extracted}</span>
                    <span className="text-[11px] font-mono text-archival-muted normal-case">
                      {rows.length} candidate{rows.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  {rows.map((c) => (
                    <label
                      key={c.id}
                      className="grid grid-cols-12 items-center gap-3 px-4 py-2.5 hover:bg-archival-soft cursor-pointer border-b border-archival last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                        className="col-span-1"
                      />
                      <div className="col-span-5 text-[13px] truncate">
                        <div className="text-archival-primary truncate">
                          {c.bill_number || <span className="text-archival-muted">no bill #</span>}
                        </div>
                        <div className="text-[11px] text-archival-muted truncate">
                          {c.original_filename}
                        </div>
                      </div>
                      <div className="col-span-3 text-[12px] text-archival-secondary">
                        {c.vendor_resolution_state?.replaceAll("_", " ")}
                      </div>
                      <div className="col-span-3 text-right">
                        <Link
                          to={`/candidates/${c.id}`}
                          className="text-[12px] text-archival-secondary hover:text-archival-primary underline-offset-4 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Open
                        </Link>
                      </div>
                    </label>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className="border border-archival rounded-md bg-archival-surface p-4 space-y-3 h-fit sticky top-20">
          <div className="flex items-center gap-2 text-archival-primary">
            <Users size={16} />
            <span className="text-[13px] font-semibold">Apply vendor to selected</span>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Search vendor</Label>
            <Input
              value={vendorQuery}
              onChange={(e) => setVendorQuery(e.target.value)}
              placeholder="Vendor name…"
              className="bg-white border-archival mt-1"
            />
          </div>
          {vendors.length > 0 && (
            <ul className="border border-archival rounded-md bg-white max-h-48 overflow-y-auto">
              {vendors.map((v) => (
                <li key={v.id}>
                  <button
                    onClick={() => setPickedVendor(v)}
                    className={`w-full text-left px-3 py-2 text-[13px] hover:bg-archival-soft ${
                      pickedVendor?.id === v.id ? "bg-archival-soft" : ""
                    }`}
                  >
                    <div className="font-medium">{v.name}</div>
                    {v.gst && <div className="text-[10px] text-archival-muted font-mono">{v.gst}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {pickedVendor && (
            <div className="rounded-md bg-archival-soft border border-archival px-3 py-2 text-[12px]">
              Selected: <span className="font-semibold">{pickedVendor.name}</span>
            </div>
          )}
          <Button
            data-testid={TID.vendorDeskApply}
            onClick={apply}
            disabled={!pickedVendor || selected.size === 0 || working}
            className="w-full text-white"
            style={{ backgroundColor: "var(--action)" }}
          >
            {working ? <Loader2 className="animate-spin mr-1" size={12} /> : null}
            Apply to {selected.size} candidate{selected.size === 1 ? "" : "s"}
          </Button>
        </aside>
      </div>
    </div>
  );
}
